import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Edit,
  Trash2,
  Copy,
  Filter,
  ArrowLeft,
  ChevronRight,
  Check,
  Loader2,
} from 'lucide-react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  ModernButton,
  ModernInput,
  ModernTextarea,
  Toast,
  ToastType,
  ConfirmDialog,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ListPageSkeleton,
  Switch,
} from '../../components/ui'
import { DateTimePicker, PriceInput } from '../../components/form'
import { apiService } from '../../services/api'
import { CommissionRule } from '../../types'
import { formatCurrency, parseCurrency, localDateTimeInputToIso8601 } from '../../utils/formatters'
import { cn } from '../../lib/utils'
import { Stepper } from '../../components/vehicles'

function normalizeCommissionPaymentRule(
  p: string | undefined
): 'after_invoice' | 'after_payment' {
  if (p === 'after_payment') return 'after_payment'
  return 'after_invoice'
}

function normalizeTierRulesInput(
  raw: unknown
): Array<{ min: number; max?: number; percentage: number }> {
  if (raw == null) return []
  if (typeof raw === 'string') {
    try {
      return normalizeTierRulesInput(JSON.parse(raw))
    } catch {
      return []
    }
  }
  if (!Array.isArray(raw)) return []
  return raw
    .filter(
      (item): item is Record<string, unknown> =>
        item !== null && typeof item === 'object' && !Array.isArray(item)
    )
    .map((item) => ({
      min: Number(item.min) || 0,
      max:
        item.max !== undefined && item.max !== null && item.max !== ''
          ? Number(item.max)
          : undefined,
      percentage: Number(item.percentage) || 0,
    }))
}

function tierHalfOpenOverlap(
  a: { min: number; max?: number },
  b: { min: number; max?: number }
): boolean {
  const endA = a.max ?? Number.POSITIVE_INFINITY
  const endB = b.max ?? Number.POSITIVE_INFINITY
  return Math.max(a.min, b.min) < Math.min(endA, endB)
}

function validateTierRulesFormNorm(
  tiers: Array<{ min: number; max?: number; percentage: number }>
): { invalidRange: Set<number>; overlap: Set<number> } {
  const invalidRange = new Set<number>()
  const overlap = new Set<number>()
  tiers.forEach((tier, i) => {
    if (tier.max != null && tier.max <= tier.min) invalidRange.add(i)
  })
  for (let i = 0; i < tiers.length; i++) {
    for (let j = i + 1; j < tiers.length; j++) {
      if (tierHalfOpenOverlap(tiers[i], tiers[j])) {
        overlap.add(i)
        overlap.add(j)
      }
    }
  }
  return { invalidRange, overlap }
}

function getTierFormValidation(formData: {
  type: string
  commissionType: string
  tierRules: unknown
}): {
  hasError: boolean
  invalidRange: Set<number>
  overlap: Set<number>
  emptyTiers: boolean
} {
  const isTierMode =
    formData.type === 'tier' &&
    (formData.commissionType === 'percentage' || formData.commissionType === 'mixed')
  if (!isTierMode) {
    return { hasError: false, invalidRange: new Set(), overlap: new Set(), emptyTiers: false }
  }
  const tiers = normalizeTierRulesInput(formData.tierRules)
  if (tiers.length === 0) {
    return { hasError: true, invalidRange: new Set(), overlap: new Set(), emptyTiers: true }
  }
  const { invalidRange, overlap } = validateTierRulesFormNorm(tiers)
  return {
    hasError: invalidRange.size > 0 || overlap.size > 0,
    invalidRange,
    overlap,
    emptyTiers: false,
  }
}

const COMMISSION_RULE_STEP_COUNT = 3

// ── Main Component ──────────────────────────────────────────────────────────

export const CommissionRulesPage: React.FC = () => {
  const { t } = useTranslation()
  const [rules, setRules] = useState<CommissionRule[]>([])
  const [filteredRules, setFilteredRules] = useState<CommissionRule[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
  const [view, setView] = useState<'list' | 'form'>('list')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
  const [selectedRule, setSelectedRule] = useState<CommissionRule | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [applicableToFilter, setApplicableToFilter] = useState<string>('all')
  const [vigenceFilter, setVigenceFilter] = useState<string>('all')

  const [paymentForms, setPaymentForms] = useState<Array<{ id: string; name: string; description?: string }>>([])
  const [salespeople, setSalespeople] = useState<Array<{ id: string; name: string }>>([])
  const [applyStoreRulesToAllSales, setApplyStoreRulesToAllSales] = useState(true)
  const [savingStoreCommissionSetting, setSavingStoreCommissionSetting] = useState(false)
  const [isSavingRule, setIsSavingRule] = useState(false)
  const [nameTouched, setNameTouched] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'percentage' as 'percentage' | 'tier',
    commissionType: 'percentage' as 'percentage' | 'fixed' | 'mixed',
    percentageValue: 0,
    fixedValue: 0,
    calculationBase: 'vehicle_value' as 'vehicle_value' | 'profit' | 'financed_value',
    applicableTo: 'salesperson' as 'salesperson' | 'manager' | 'dealership',
    paymentRule: 'after_invoice' as 'after_invoice' | 'after_payment',
    startDate: '',
    endDate: '',
    vehicleType: '' as '' | 'new' | 'used',
    paymentFormId: '',
    salespersonId: '',
    maxCommission: 0,
    tierRules: [] as Array<{ min: number; max?: number; percentage: number }>,
    isActive: true,
    isMandatory: false,
    sharedAmongAllSalespeople: false,
    ruleBonuses: [] as Array<{ label: string; amount: number }>,
  })

  const tierFormValidation = useMemo(() => getTierFormValidation(formData), [
    formData.type,
    formData.commissionType,
    formData.tierRules,
  ])

  const commissionStepLabels = useMemo(
    () => [
      t('financialSales.commissionRuleForm.stepBasic'),
      t('financialSales.commissionRuleForm.stepAdvanced'),
      t('financialSales.commissionRuleForm.stepValidity'),
    ],
    [t]
  )

  const tryGoToCommissionStep = (targetIndex: number) => {
    if (targetIndex > currentStep && tierFormValidation.hasError) {
      setToast({
        message: t('financialSales.commissionRuleForm.tierFixBeforeContinue'),
        type: 'error',
      })
      return
    }
    if (targetIndex > currentStep && currentStep === 0 && !formData.name.trim()) {
      setNameTouched(true)
      setToast({
        message: t('financialSales.commissionRuleForm.nameRequired'),
        type: 'error',
      })
      return
    }
    setCurrentStep(targetIndex)
  }

  const handleStepperClick = (stepOneBased: number) => {
    tryGoToCommissionStep(stepOneBased - 1)
  }

  const handleNextCommissionStep = () => {
    tryGoToCommissionStep(currentStep + 1)
  }

  useEffect(() => {
    fetchRules()
    fetchPaymentForms()
    void fetchSalespeople()
  }, [])

  const fetchSalespeople = async () => {
    try {
      const res = await apiService.getUsers(200, 1)
      const list = (res.users || []).filter(
        (u) => u.status === 'active' && u.dealershipRole === 'salesperson'
      )
      setSalespeople(list.map((u) => ({ id: u.id, name: u.name })))
    } catch {
      setSalespeople([])
    }
  }

  const fetchPaymentForms = async () => {
    try {
      const response = await apiService.getPaymentForms()
      setPaymentForms(response || [])
    } catch (err) {
      console.error('Erro ao carregar formas de pagamento:', err)
    }
  }

  useEffect(() => {
    applyFilters()
  }, [rules, statusFilter, typeFilter, applicableToFilter, vigenceFilter])

  const fetchRules = async () => {
    try {
      setIsLoading(true)
      const [response, me] = await Promise.all([
        apiService.getCommissionRules(),
        apiService.getDealershipMe().catch(() => null),
      ])
      setRules(response)
      if (me) {
        setApplyStoreRulesToAllSales(me.commissionApplyStoreRulesToAllSales !== false)
      }
    } catch (err) {
      console.error('Erro ao carregar regras de comissão:', err)
      setToast({ message: t('financialSales.commissionRulesPage.loadError'), type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleApplyStoreRulesChange = async (checked: boolean) => {
    setSavingStoreCommissionSetting(true)
    try {
      await apiService.patchDealershipMe({ commissionApplyStoreRulesToAllSales: checked })
      setApplyStoreRulesToAllSales(checked)
      setToast({ message: t('financialSales.commissionSettings.saveSuccess'), type: 'success' })
    } catch (err) {
      console.error(err)
      setToast({ message: t('financialSales.commissionSettings.saveError'), type: 'error' })
    } finally {
      setSavingStoreCommissionSetting(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...rules]
    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => statusFilter === 'active' ? r.isActive : !r.isActive)
    }
    if (typeFilter !== 'all') {
      filtered = filtered.filter((r) => r.commissionType === typeFilter)
    }
    if (applicableToFilter !== 'all') {
      filtered = filtered.filter((r) => r.applicableTo === applicableToFilter)
    }
    if (vigenceFilter !== 'all') {
      const now = new Date()
      filtered = filtered.filter((r) => {
        if (vigenceFilter === 'active') {
          const start = r.startDate ? new Date(r.startDate) : null
          const end = r.endDate ? new Date(r.endDate) : null
          return (!start || start <= now) && (!end || end >= now)
        } else if (vigenceFilter === 'expired') {
          const end = r.endDate ? new Date(r.endDate) : null
          return end && end < now
        }
        return true
      })
    }
    setFilteredRules(filtered)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'percentage',
      commissionType: 'percentage',
      percentageValue: 0,
      fixedValue: 0,
      calculationBase: 'vehicle_value',
      applicableTo: 'salesperson',
      paymentRule: 'after_invoice',
      startDate: '',
      endDate: '',
      vehicleType: '',
      paymentFormId: '',
      salespersonId: '',
      maxCommission: 0,
      tierRules: [],
      isActive: true,
      isMandatory: false,
      sharedAmongAllSalespeople: false,
      ruleBonuses: [],
    })
    setNameTouched(false)
    setCurrentStep(0)
  }

  const handleCreate = () => {
    resetForm()
    setIsEditing(false)
    setSelectedRule(null)
    setView('form')
  }

  const handleEdit = (rule: CommissionRule) => {
    setSelectedRule(rule)
    setFormData({
      name: rule.name,
      description: rule.description || '',
      type: rule.type || 'percentage',
      commissionType: rule.commissionType || 'percentage',
      percentageValue: rule.percentageValue || 0,
      fixedValue: rule.fixedValue || 0,
      calculationBase: rule.calculationBase || 'vehicle_value',
      applicableTo: rule.applicableTo || 'salesperson',
      paymentRule: normalizeCommissionPaymentRule(rule.paymentRule),
      startDate: rule.startDate || '',
      endDate: rule.endDate || '',
      vehicleType: rule.vehicleType || '',
      paymentFormId: rule.paymentFormId || '',
      salespersonId: rule.salespersonId || '',
      maxCommission: rule.maxCommission || 0,
      tierRules: normalizeTierRulesInput(rule.tierRules),
      isActive: rule.isActive,
      isMandatory: rule.isMandatory ?? false,
      sharedAmongAllSalespeople: rule.sharedAmongAllSalespeople ?? false,
      ruleBonuses: (rule.ruleBonuses ?? []).map((b) => ({
        label: b.label,
        amount:
          typeof b.amount === 'string' ? parseFloat(b.amount) || 0 : Number(b.amount) || 0,
      })),
    })
    setCurrentStep(0)
    setNameTouched(false)
    setIsEditing(true)
    setView('form')
  }

  const handleDuplicate = async () => {
    if (!selectedRule) return
    try {
      await apiService.duplicateCommissionRule(selectedRule.id)
      setToast({ message: t('financialSales.commissionRulesPage.duplicateSuccess'), type: 'success' })
      await fetchRules()
      setDuplicateDialogOpen(false)
      setSelectedRule(null)
    } catch (err) {
      console.error('Erro ao duplicar regra de comissão:', err)
      setToast({ message: t('financialSales.commissionRulesPage.duplicateError'), type: 'error' })
    }
  }

  const persistRule = async () => {
    if (isSavingRule) return
    if (!formData.name.trim()) {
      setNameTouched(true)
      setToast({
        message: t('financialSales.commissionRuleForm.nameRequired'),
        type: 'error',
      })
      return
    }
    if (tierFormValidation.hasError) {
      setToast({
        message: t('financialSales.commissionRuleForm.tierFixBeforeContinue'),
        type: 'error',
      })
      return
    }
    setIsSavingRule(true)
    try {
      const dataToSubmit: Record<string, unknown> = {
        ...formData,
        paymentFormId: formData.paymentFormId || undefined,
        startDate: localDateTimeInputToIso8601(formData.startDate),
        endDate: localDateTimeInputToIso8601(formData.endDate),
        vehicleType:
          formData.vehicleType === 'new' || formData.vehicleType === 'used'
            ? formData.vehicleType
            : undefined,
      }
      delete dataToSubmit.salespersonId
      if (formData.sharedAmongAllSalespeople) {
        dataToSubmit.salespersonId = null
      } else if (formData.salespersonId) {
        dataToSubmit.salespersonId = formData.salespersonId
      } else if (isEditing) {
        dataToSubmit.salespersonId = null
      }
      if (formData.commissionType === 'fixed') {
        dataToSubmit.type = 'fixed'
        delete dataToSubmit.percentageValue
        delete dataToSubmit.tierRules
      } else if (formData.type === 'tier') {
        dataToSubmit.tierRules = normalizeTierRulesInput(formData.tierRules)
      } else {
        delete dataToSubmit.tierRules
      }
      dataToSubmit.ruleBonuses = formData.ruleBonuses
        .filter((b) => b.label.trim())
        .map((b, i) => ({
          label: b.label.trim(),
          amount: Number(b.amount) >= 0 ? Number(b.amount) : 0,
          sortOrder: i,
        }))
      if (isEditing && selectedRule) {
        await apiService.updateCommissionRule(selectedRule.id, dataToSubmit)
        setToast({ message: t('financialSales.commissionRulesPage.updateSuccess'), type: 'success' })
      } else {
        await apiService.createCommissionRule(dataToSubmit)
        setToast({ message: t('financialSales.commissionRulesPage.createSuccess'), type: 'success' })
      }
      await fetchRules()
      setIsEditing(false)
      setSelectedRule(null)
      setView('list')
      resetForm()
    } catch (err) {
      console.error('Erro ao salvar regra de comissão:', err)
      setToast({ message: t('financialSales.commissionRulesPage.saveRuleError'), type: 'error' })
    } finally {
      setIsSavingRule(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedRule) return
    try {
      await apiService.deleteCommissionRule(selectedRule.id)
      setToast({ message: t('financialSales.commissionRulesPage.deleteSuccess'), type: 'success' })
      await fetchRules()
      setDeleteDialogOpen(false)
      setSelectedRule(null)
    } catch (err) {
      console.error('Erro ao excluir regra de comissão:', err)
      setToast({ message: t('financialSales.commissionRulesPage.deleteError'), type: 'error' })
    }
  }

  const addTierRule = () => {
    setFormData({ ...formData, tierRules: [...formData.tierRules, { min: 0, max: undefined, percentage: 0 }] })
  }

  const removeTierRule = (index: number) => {
    setFormData({ ...formData, tierRules: formData.tierRules.filter((_, i) => i !== index) })
  }

  const updateTierRule = (index: number, field: string, value: number | undefined) => {
    const updated = [...formData.tierRules]
    updated[index] = { ...updated[index], [field]: value }
    setFormData({ ...formData, tierRules: updated })
  }

  const addRuleBonusRow = () => {
    setFormData({
      ...formData,
      ruleBonuses: [...formData.ruleBonuses, { label: '', amount: 0 }],
    })
  }

  const removeRuleBonusRow = (index: number) => {
    setFormData({
      ...formData,
      ruleBonuses: formData.ruleBonuses.filter((_, i) => i !== index),
    })
  }

  const updateRuleBonusRow = (index: number, field: 'label' | 'amount', value: string | number) => {
    const next = [...formData.ruleBonuses]
    next[index] = { ...next[index], [field]: value }
    setFormData({ ...formData, ruleBonuses: next })
  }

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      percentage: t('financial.percentage'),
      fixed: t('financial.fixed'),
      mixed: t('financial.mixed'),
    }
    return labels[type] || type
  }

  const getApplicableToLabel = (applicableTo: string) => {
    const labels: Record<string, string> = {
      salesperson: t('financial.salesperson'),
      manager: t('financial.manager'),
      dealership: t('financial.dealership'),
    }
    return labels[applicableTo] || applicableTo
  }

  const salespersonName = (id: string | null | undefined) => {
    if (!id) return t('financialSales.commissionRuleForm.salespersonAll')
    return salespeople.find((s) => s.id === id)?.name || id
  }

  const getVigenceStatus = (rule: CommissionRule) => {
    if (!rule.startDate && !rule.endDate) {
      return t('financialSales.commissionRuleForm.vigenceStatusNone')
    }
    const now = new Date()
    const start = rule.startDate ? new Date(rule.startDate) : null
    const end = rule.endDate ? new Date(rule.endDate) : null
    if (start && start > now) return t('financialSales.commissionRuleForm.vigenceStatusFuture')
    if (end && end < now) return t('financialSales.commissionRuleForm.vigenceStatusExpired')
    return t('financialSales.commissionRuleForm.vigenceStatusActive')
  }

  const vigenceStatusIsCurrent = (rule: CommissionRule): boolean => {
    if (!rule.startDate && !rule.endDate) return false
    const now = new Date()
    const start = rule.startDate ? new Date(rule.startDate) : null
    const end = rule.endDate ? new Date(rule.endDate) : null
    if (start && start > now) return false
    if (end && end < now) return false
    return true
  }

  const goBack = () => {
    setView('list')
    setIsEditing(false)
    setSelectedRule(null)
    setCurrentStep(0)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="h-8 w-56 rounded-md bg-border animate-pulse" />
          <div className="h-9 w-40 rounded-md bg-border animate-pulse" />
        </div>
        <ListPageSkeleton variant="table" rows={8} showFilters={true} showStats={false} />
      </div>
    )
  }

  // ── FORM VIEW ──────────────────────────────────────────────────────────────
  if (view === 'form') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <ModernButton variant="outline" size="sm" onClick={goBack} className="w-fit shrink-0">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('actions.back')}
          </ModernButton>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-foreground font-display">
              {isEditing
                ? t('financialSales.commissionRulesPage.formTitleEdit')
                : t('financialSales.commissionRulesPage.formTitleNew')}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isEditing && selectedRule?.name
                ? t('financialSales.commissionRulesPage.formSubtitleEdit', { name: selectedRule.name })
                : t('financialSales.commissionRulesPage.formSubtitleNew')}
            </p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
          }}
        >
          <div className="mb-4">
            <Stepper
              currentStep={currentStep + 1}
              totalSteps={COMMISSION_RULE_STEP_COUNT}
              steps={commissionStepLabels}
              onStepClick={handleStepperClick}
            />
          </div>

          {/* Step Content */}
          <Card className="overflow-hidden border-border/80 shadow-sm">
            <CardHeader className="border-b border-border/60 bg-muted/20 pb-4">
              <CardTitle className="text-lg font-semibold text-foreground">
                {commissionStepLabels[currentStep]}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                {currentStep === 0 && t('financial.basicConfig')}
                {currentStep === 1 && t('financial.advancedRules')}
                {currentStep === 2 && t('financial.validity')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5 p-6">

              {currentStep === 0 && (
                <div className="space-y-4">
                  <ModernInput
                    required
                    label={`${t('financialSales.commissionRuleForm.nameField')} *`}
                    value={formData.name}
                    onBlur={() => setNameTouched(true)}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    error={
                      nameTouched && !formData.name.trim()
                        ? t('financialSales.commissionRuleForm.nameRequired')
                        : undefined
                    }
                    placeholder={t('financialSales.commissionRuleForm.namePlaceholder')}
                  />
                  <ModernTextarea
                    label={t('financialSales.commissionRuleForm.descriptionField')}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder={t('financialSales.commissionRuleForm.descriptionPlaceholder')}
                  />
                  <div>
                    <Label>{t('financialSales.commissionRuleForm.commissionKindLabel')} *</Label>
                    <Select
                      value={formData.commissionType}
                      onValueChange={(value: 'percentage' | 'fixed' | 'mixed') => {
                        setFormData((prev) => {
                          if (value === 'fixed') {
                            return { ...prev, commissionType: value, type: 'percentage' as 'percentage' | 'tier' }
                          }
                          return {
                            ...prev,
                            commissionType: value,
                            type: prev.type,
                          }
                        })
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">{t('financial.percentage')}</SelectItem>
                        <SelectItem value="fixed">{t('financial.fixed')}</SelectItem>
                        <SelectItem value="mixed">{t('financialSales.commissionRuleForm.mixedOption')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {(formData.commissionType === 'percentage' || formData.commissionType === 'mixed') && (
                    <>
                      <div>
                        <Label>{t('financialSales.commissionRuleForm.calculationModeLabel')}</Label>
                        <Select
                          value={formData.type}
                          onValueChange={(value: 'percentage' | 'tier') =>
                            setFormData({ ...formData, type: value })
                          }
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">
                              {t('financialSales.commissionRuleForm.percentSingle')}
                            </SelectItem>
                            <SelectItem value="tier">{t('financialSales.commissionRuleForm.tierMode')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {formData.type === 'percentage' && (
                        <ModernInput
                          type="number"
                          step="0.01"
                          min={0}
                          max={100}
                          label={`${t('financialSales.commissionRuleForm.percentValueLabel')} *`}
                          value={String(formData.percentageValue)}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              percentageValue: parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      )}

                      {formData.type === 'tier' && (
                        <div className="space-y-3">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <Label>{t('financialSales.commissionRuleForm.tiersTitle')} *</Label>
                            <ModernButton type="button" onClick={addTierRule} variant="outline" size="sm" className="shrink-0">
                              <Plus className="h-4 w-4 mr-1" />
                              {t('financialSales.commissionRuleForm.addTierRow')}
                            </ModernButton>
                          </div>
                          {tierFormValidation.hasError && (
                            <p className="text-sm font-medium text-destructive">
                              {tierFormValidation.emptyTiers
                                ? t('financialSales.commissionRuleForm.tierEmpty')
                                : t('financialSales.commissionRuleForm.tierFixBeforeContinue')}
                            </p>
                          )}
                          <div className="space-y-3">
                            {formData.tierRules.map((tier, index) => {
                              const rowInvalidRange = tierFormValidation.invalidRange.has(index)
                              const rowOverlap = tierFormValidation.overlap.has(index)
                              const rowError = rowInvalidRange || rowOverlap
                              return (
                                <div
                                  key={index}
                                  className={cn(
                                    'grid w-full min-w-0 grid-cols-1 gap-3 rounded-lg border p-3 sm:grid-cols-12 sm:items-end',
                                    rowError
                                      ? 'border-destructive bg-destructive/5'
                                      : 'border-border bg-muted/15'
                                  )}
                                >
                                  <div className="min-w-0 sm:col-span-3">
                                    <Label className="text-xs">{t('financialSales.commissionRuleForm.tierMin')}</Label>
                                    <PriceInput
                                      value={tier.min > 0 ? formatCurrency(tier.min) : ''}
                                      onChange={(value) => {
                                        const parsed = parseCurrency(value)
                                        updateTierRule(index, 'min', parsed || 0)
                                      }}
                                      placeholder="0,00"
                                    />
                                  </div>
                                  <div className="min-w-0 sm:col-span-3">
                                    <Label className="text-xs">{t('financialSales.commissionRuleForm.tierMax')}</Label>
                                    <PriceInput
                                      value={tier.max ? formatCurrency(tier.max) : ''}
                                      onChange={(value) => {
                                        const parsed = parseCurrency(value)
                                        updateTierRule(index, 'max', parsed || undefined)
                                      }}
                                      placeholder="Sem limite"
                                    />
                                  </div>
                                  <div className="min-w-0 sm:col-span-3">
                                    <Label className="text-xs">{t('financialSales.commissionRuleForm.tierPercent')}</Label>
                                    <ModernInput
                                      type="number"
                                      step="0.01"
                                      min={0}
                                      max={100}
                                      value={String(tier.percentage)}
                                      onChange={(e) =>
                                        updateTierRule(index, 'percentage', parseFloat(e.target.value) || 0)
                                      }
                                    />
                                  </div>
                                  <div className="flex justify-end sm:col-span-3">
                                    <ModernButton
                                      type="button"
                                      onClick={() => removeTierRule(index)}
                                      variant="outline"
                                      size="sm"
                                      className="w-full sm:w-auto"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </ModernButton>
                                  </div>
                                  {rowInvalidRange && (
                                    <p className="text-xs text-destructive sm:col-span-12">
                                      {t('financialSales.commissionRuleForm.tierInvalidRange')}
                                    </p>
                                  )}
                                  {rowOverlap && !rowInvalidRange && (
                                    <p className="text-xs text-destructive sm:col-span-12">
                                      {t('financialSales.commissionRuleForm.tierOverlap')}
                                    </p>
                                  )}
                                </div>
                              )
                            })}
                            {formData.tierRules.length === 0 && (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                {t('financialSales.commissionRuleForm.tierHintEmpty')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {(formData.commissionType === 'fixed' || formData.commissionType === 'mixed') && (
                    <div>
                      <Label>{t('financialSales.commissionRuleForm.fixedBlockLabel')} *</Label>
                      <PriceInput
                        value={formData.fixedValue > 0 ? formatCurrency(formData.fixedValue) : ''}
                        onChange={(value) => {
                          const parsed = parseCurrency(value)
                          setFormData({ ...formData, fixedValue: parsed || 0 })
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  )}

                  <div>
                    <Label>{t('financialSales.commissionRuleForm.calculationBase')} *</Label>
                    <Select
                      value={formData.calculationBase}
                      onValueChange={(value: 'vehicle_value' | 'profit' | 'financed_value') =>
                        setFormData({ ...formData, calculationBase: value })
                      }
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vehicle_value">
                          {t('financialSales.commissionRuleForm.calcBaseVehicle')}
                        </SelectItem>
                        <SelectItem value="profit">{t('financialSales.commissionRuleForm.calcBaseProfit')}</SelectItem>
                        <SelectItem value="financed_value">
                          {t('financialSales.commissionRuleForm.calcBaseFinanced')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{t('financialSales.commissionRuleForm.applicableToField')} *</Label>
                    <Select
                      value={formData.applicableTo}
                      onValueChange={(value: 'salesperson' | 'manager' | 'dealership') =>
                        setFormData({
                          ...formData,
                          applicableTo: value,
                          ...(value !== 'salesperson' ? { sharedAmongAllSalespeople: false } : {}),
                        })
                      }
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="salesperson">{t('financial.salesperson')}</SelectItem>
                        <SelectItem value="manager">{t('financial.manager')}</SelectItem>
                        <SelectItem value="dealership">{t('financial.dealership')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{t('financialSales.commissionRuleForm.paymentRuleField')} *</Label>
                    <Select
                      value={formData.paymentRule}
                      onValueChange={(value: 'after_invoice' | 'after_payment') =>
                        setFormData({ ...formData, paymentRule: value })
                      }
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="after_invoice">
                          {t('financialSales.commissions.paymentRuleAfterInvoice')}
                        </SelectItem>
                        <SelectItem value="after_payment">
                          {t('financialSales.commissions.paymentRuleAfterPayment')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/10 p-4">
                    <Switch
                      id="commission-rule-active"
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                    />
                    <div className="min-w-0 space-y-0.5">
                      <Label htmlFor="commission-rule-active" className="cursor-pointer text-sm font-medium">
                        {t('financialSales.commissionRuleForm.activeRuleLabel')}
                      </Label>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-muted/10 p-4">
                    <div className="flex items-start gap-3">
                      <Switch
                        id="commission-rule-mandatory"
                        checked={formData.isMandatory}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, isMandatory: checked })
                        }
                      />
                      <div className="min-w-0 space-y-0.5">
                        <Label htmlFor="commission-rule-mandatory" className="cursor-pointer text-sm font-medium">
                          {t('financial.isMandatoryCommission')}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t('financial.isMandatoryCommissionHint')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {formData.applicableTo === 'salesperson' && (
                    <div className="flex items-center gap-3 pt-1">
                      <Switch
                        checked={formData.sharedAmongAllSalespeople}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            sharedAmongAllSalespeople: checked,
                            salespersonId: checked ? '' : formData.salespersonId,
                          })
                        }
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {t('financialSales.commissionRuleForm.sharedAmongLabel')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('financialSales.commissionRuleForm.sharedAmongHint')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 1: Regras Avançadas */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <Label>{t('financialSales.commissionRuleForm.vehicleTypeField')}</Label>
                    <Select
                      value={formData.vehicleType || 'all'}
                      onValueChange={(value: 'all' | 'new' | 'used') =>
                        setFormData({ ...formData, vehicleType: value === 'all' ? '' : value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('financialSales.commissionRuleForm.vehicleAll')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('financialSales.commissionRuleForm.vehicleAll')}</SelectItem>
                        <SelectItem value="new">{t('financialSales.commissionRuleForm.vehicleNew')}</SelectItem>
                        <SelectItem value="used">{t('financialSales.commissionRuleForm.vehicleUsed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('financialSales.commissionRuleForm.paymentFormField')}</Label>
                    <Select
                      value={formData.paymentFormId || 'none'}
                      onValueChange={(value) =>
                        setFormData({ ...formData, paymentFormId: value === 'none' ? '' : value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('financialSales.commissionRuleForm.paymentPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('financialSales.commissionRuleForm.paymentNone')}</SelectItem>
                        {paymentForms.map((form) => (
                          <SelectItem key={form.id} value={form.id}>
                            {form.name}{form.description ? ` — ${form.description}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('financialSales.commissionRuleForm.paymentFormHint')}
                    </p>
                  </div>
                  <div>
                    <Label>{t('financialSales.commissionRuleForm.salespersonLabel')}</Label>
                    <Select
                      value={formData.salespersonId || 'none'}
                      onValueChange={(value) =>
                        setFormData({ ...formData, salespersonId: value === 'none' ? '' : value })
                      }
                      disabled={formData.sharedAmongAllSalespeople}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('financialSales.commissionRuleForm.salespersonAll')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('financialSales.commissionRuleForm.salespersonAll')}</SelectItem>
                        {salespeople.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('financialSales.commissionRuleForm.salespersonHint')}
                    </p>
                  </div>
                  <div>
                    <Label>{t('financialSales.commissionRuleForm.maxCommissionField')}</Label>
                    <PriceInput
                      value={formData.maxCommission > 0 ? formatCurrency(formData.maxCommission) : ''}
                      onChange={(value) => {
                        const parsed = parseCurrency(value)
                        setFormData({ ...formData, maxCommission: parsed || 0 })
                      }}
                      placeholder={t('financialSales.commissionRuleForm.maxCommissionPlaceholder')}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('financialSales.commissionRuleForm.maxCommissionHelp')}
                    </p>
                  </div>
                  <div className="rounded-lg border border-border bg-muted/10 p-4 space-y-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t('financialSales.commissionRuleForm.ruleBonusTitle')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t('financialSales.commissionRuleForm.ruleBonusHint')}
                      </p>
                    </div>
                    {formData.ruleBonuses.map((row, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end border-b border-border/60 pb-3 last:border-0 last:pb-0"
                      >
                        <div className="flex-1 min-w-[160px]">
                          <Label className="text-xs text-muted-foreground">
                            {t('financialSales.commissions.bonusLabelPlaceholder')}
                          </Label>
                          <ModernInput
                            value={row.label}
                            onChange={(e) => updateRuleBonusRow(idx, 'label', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div className="w-full sm:w-40">
                          <Label className="text-xs text-muted-foreground">
                            {t('financialSales.commissions.bonusAmountLabel')}
                          </Label>
                          <PriceInput
                            value={row.amount > 0 ? formatCurrency(row.amount) : ''}
                            onChange={(v) => {
                              const parsed = parseCurrency(v)
                              updateRuleBonusRow(idx, 'amount', parsed || 0)
                            }}
                            className="mt-1"
                            placeholder="0,00"
                          />
                        </div>
                        <ModernButton
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-destructive sm:mb-0.5"
                          onClick={() => removeRuleBonusRow(idx)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </ModernButton>
                      </div>
                    ))}
                    <ModernButton type="button" variant="outline" size="sm" onClick={addRuleBonusRow}>
                      <Plus className="h-4 w-4 mr-1" />
                      {t('financialSales.commissions.bonusAdd')}
                    </ModernButton>
                  </div>
                </div>
              )}

              {/* Step 2: Vigência */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <DateTimePicker
                    label={t('financialSales.commissionRuleForm.startAt')}
                    value={formData.startDate}
                    onChange={(value) => setFormData({ ...formData, startDate: value })}
                    placeholder={t('financialSales.commissionRuleForm.startPlaceholder')}
                  />
                  <DateTimePicker
                    label={t('financialSales.commissionRuleForm.endAt')}
                    value={formData.endDate}
                    onChange={(value) => setFormData({ ...formData, endDate: value })}
                    placeholder={t('financialSales.commissionRuleForm.endPlaceholder')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('financialSales.commissionRuleForm.validityHint')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex flex-col-reverse gap-3 border-t border-border/60 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              {currentStep > 0 && (
                <ModernButton type="button" variant="outline" onClick={() => setCurrentStep((s) => s - 1)}>
                  {t('actions.previous')}
                </ModernButton>
              )}
            </div>
            <div className="flex flex-wrap gap-3 sm:justify-end">
              <ModernButton type="button" variant="outline" onClick={goBack}>
                {t('actions.cancel')}
              </ModernButton>
              {currentStep < COMMISSION_RULE_STEP_COUNT - 1 ? (
                <ModernButton
                  type="button"
                  disabled={currentStep === 0 && tierFormValidation.hasError}
                  onClick={handleNextCommissionStep}
                >
                  {t('actions.next')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </ModernButton>
              ) : (
                <ModernButton
                  type="button"
                  disabled={isSavingRule || tierFormValidation.hasError}
                  onClick={() => void persistRule()}
                >
                  {isSavingRule ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden />
                  ) : (
                    <Check className="h-4 w-4 mr-1.5" />
                  )}
                  {isSavingRule
                    ? t('actions.loading')
                    : isEditing
                      ? t('financialSales.commissionRuleForm.updateRuleCta')
                      : t('financialSales.commissionRuleForm.createRuleCta')}
                </ModernButton>
              )}
            </div>
          </div>
        </form>

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={3000} />}
      </div>
    )
  }

  // ── LIST VIEW ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground font-display">
            {t('financial.commissionRules')}
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('financialSales.commissionRulesPage.listSubtitle')}
          </p>
        </div>
        <ModernButton onClick={handleCreate} className="w-full shrink-0 sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          {t('financial.newRule')}
        </ModernButton>
      </div>

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="border-b border-border/60 bg-muted/10 pb-4">
          <CardTitle className="text-base font-semibold">{t('financialSales.commissionSettings.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1 max-w-xl">
            <p className="text-sm font-medium text-foreground">
              {t('financialSales.commissionSettings.applyStoreRules')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('financialSales.commissionSettings.applyStoreHint')}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Switch
              checked={applyStoreRulesToAllSales}
              onCheckedChange={(v) => void handleApplyStoreRulesChange(v)}
              disabled={savingStoreCommissionSetting}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="border-b border-border/60 bg-muted/10 pb-4">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Filter className="h-4 w-4 text-muted-foreground" />
            {t('financialSales.commissionRulesPage.filtersTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <Label>{t('financialSales.commissionRulesPage.filterStatus')}</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('financialSales.commissionRulesPage.allMasc')}</SelectItem>
                  <SelectItem value="active">{t('financialSales.commissionRulesPage.activeRules')}</SelectItem>
                  <SelectItem value="inactive">{t('financialSales.commissionRulesPage.inactiveRules')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('financialSales.commissionRulesPage.filterType')}</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('financialSales.commissionRulesPage.allMasc')}</SelectItem>
                  <SelectItem value="percentage">{t('financial.percentage')}</SelectItem>
                  <SelectItem value="fixed">{t('financial.fixed')}</SelectItem>
                  <SelectItem value="mixed">{t('financial.mixed')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('financialSales.commissionRulesPage.filterApplicable')}</Label>
              <Select value={applicableToFilter} onValueChange={setApplicableToFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('financialSales.commissionRulesPage.allMasc')}</SelectItem>
                  <SelectItem value="salesperson">{t('financial.salesperson')}</SelectItem>
                  <SelectItem value="manager">{t('financial.manager')}</SelectItem>
                  <SelectItem value="dealership">{t('financial.dealership')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('financialSales.commissionRulesPage.filterVigence')}</Label>
              <Select value={vigenceFilter} onValueChange={setVigenceFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('financialSales.commissionRulesPage.allFem')}</SelectItem>
                  <SelectItem value="active">{t('financialSales.commissionRulesPage.vigenceCurrent')}</SelectItem>
                  <SelectItem value="expired">{t('financialSales.commissionRulesPage.vigencePast')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="border-b border-border/60 bg-muted/10 pb-4">
          <CardTitle className="text-base font-semibold">
            {t('financialSales.commissionRulesPage.registeredRules', { count: filteredRules.length })}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {filteredRules.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('financialSales.commissionRulesPage.emptyList')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('financialSales.commissionRulesPage.colName')}</TableHead>
                  <TableHead>{t('financialSales.commissionRulesPage.colType')}</TableHead>
                  <TableHead>{t('financialSales.commissionRulesPage.colValue')}</TableHead>
                  <TableHead>{t('financialSales.commissions.bonusValuesColumn')}</TableHead>
                  <TableHead>{t('financialSales.commissionRulesPage.colApplicable')}</TableHead>
                  <TableHead>{t('financialSales.commissionRuleForm.listSalespersonColumn')}</TableHead>
                  <TableHead>{t('financialSales.commissionRulesPage.colStatus')}</TableHead>
                  <TableHead>{t('financialSales.commissionRulesPage.colVigence')}</TableHead>
                  <TableHead>{t('financialSales.commissionRulesPage.colActions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-wrap items-center gap-2">
                        <span>{rule.name}</span>
                        {rule.isMandatory && (
                          <Badge variant="warning" className="text-xs">
                            {t('financial.mandatoryRuleBadge')}
                          </Badge>
                        )}
                        {rule.applicableTo === 'salesperson' && rule.sharedAmongAllSalespeople && (
                          <Badge variant="info" className="text-xs">
                            {t('financialSales.commissionRuleForm.sharedBadge')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getTypeLabel(rule.commissionType)}</TableCell>
                    <TableCell>
                      {rule.commissionType === 'percentage' || rule.commissionType === 'mixed'
                        ? `${rule.percentageValue || 0}%`
                        : formatCurrency(rule.fixedValue || 0)}
                      {rule.commissionType === 'mixed' && rule.fixedValue
                        ? ` + ${formatCurrency(rule.fixedValue)}`
                        : ''}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {(rule.ruleBonuses ?? []).length > 0
                        ? (rule.ruleBonuses ?? [])
                            .map((b) => `${b.label}: ${formatCurrency(Number(b.amount) || 0)}`)
                            .join(' | ')
                        : t('financialSales.commissionRulesPage.bonusDash')}
                    </TableCell>
                    <TableCell>{getApplicableToLabel(rule.applicableTo)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {salespersonName(rule.salespersonId)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={rule.isActive ? 'success' : 'gray'}>
                        {rule.isActive
                          ? t('financialSales.commissionRulesPage.statusOn')
                          : t('financialSales.commissionRulesPage.statusOff')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={vigenceStatusIsCurrent(rule) ? 'success' : 'gray'}>
                        {getVigenceStatus(rule)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <ModernButton variant="outline" size="sm" onClick={() => handleEdit(rule)}>
                          <Edit className="h-4 w-4" />
                        </ModernButton>
                        <ModernButton variant="outline" size="sm" onClick={() => { setSelectedRule(rule); setDuplicateDialogOpen(true) }}>
                          <Copy className="h-4 w-4" />
                        </ModernButton>
                        <ModernButton variant="outline" size="sm" onClick={() => { setSelectedRule(rule); setDeleteDialogOpen(true) }}>
                          <Trash2 className="h-4 w-4" />
                        </ModernButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={3000} />}

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        title={t('financialSales.commissionRulesPage.deleteTitle')}
        description={t('financialSales.commissionRulesPage.deleteDescription', {
          name: selectedRule?.name ?? '',
        })}
        onConfirm={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />

      <ConfirmDialog
        isOpen={duplicateDialogOpen}
        title={t('financialSales.commissionRulesPage.duplicateTitle')}
        description={t('financialSales.commissionRulesPage.duplicateDescription', {
          name: selectedRule?.name ?? '',
        })}
        onConfirm={handleDuplicate}
        onClose={() => setDuplicateDialogOpen(false)}
      />
    </div>
  )
}
